import { CASE_H, FLOOR_H } from '../level.js';
import { sizedSprite, stripStyle } from '../print.jsx';
import { TORSO_REACH, castReader, compile, strip } from '../sprites.js';
import './harvest.css';

// Harvest: the first of November to the end of Thanksgiving.
//
// The reader is a scarecrow for the month, and the room has a garland of
// leaves along the top of every bookcase, a bundle of ornamental corn hung in
// every gap, hay bales with a pumpkin on them at either end, a potted mum, a
// turkey, a basket of apples, and leaves coming down all the while - with a
// skein of geese going south across the top of the screen now and then. Every
// colour below is off the console's chart, like everything else in the level -
// see palette.js.

// The fourth Thursday of November. The first Thursday falls somewhere in the
// first seven days, and the fourth is three weeks after it. This is the
// American Thanksgiving; Canada's is in October and is somebody else's season.
export function thanksgiving(year) {
  const first = new Date(year, 10, 1).getDay();
  return [11, 1 + ((4 - first + 7) % 7) + 21];
}

// --- the scarecrow -----------------------------------------------------------

// The reader's 24 by 16, so the torsos and legs fit under it unchanged. A
// straw hat with a red band and a brim wider than his head, straw sticking out
// under it, a burlap face with button eyes and a stitched mouth, and twine
// round the neck where the sack is tied on.
const SCARECROW_HEAD = [
  '.......kkkkkkkkkk.......',
  '......kHHHHHHHHHHk......',
  '......kHhHHHHHHHHk......',
  '......kRRRRRRRRRRk......',
  '.kkkkkHHHHHHHHHHHHkkkkk.',
  'kHHHHHHHHHHHHHHHHHHHHHHk',
  '.kkkkkkkkkkkkkkkkkkkkkk.',
  'yy.kssssssssssssssssk.yy',
  '.y.kssssssssssssssssk.y.',
  '...kssskksssssskksssk...',
  '...kssskksssssskksssk...',
  '...kssssssssssssssssk...',
  '...ksssssssOOsssssssk...',
  '...kssksssssssssskssk...',
  '....ksskkkkkkkkkkssk....',
  '......yRRRRRRRRRRy......',
];

const SCARECROW_KEY = {
  k: '#1c1c1c',
  H: '#ac7c00', // the hat
  h: '#f8d878', // and the light on it
  R: '#a81000', // its band, and the twine
  y: '#f8d878', // straw
  s: '#fca044', // burlap - the face, and the hand he reaches with
  O: '#881400', // a button for a nose
  t: '#f83800', // a red flannel shirt
  u: '#a81000',
  // Trousers. Patched denim rather than the reader's grey, which is the one
  // other colour that measures against the floor: anything darker is the
  // boards' own and his legs are not there. This is 5.6:1 against them.
  p: '#3cbcfc',
  b: '#f8b800', // the book he carries, unchanged - it is the same book
  w: '#f8f8f8',
};

// A scarecrow's arms are out to the side on his pole, and the reaching torso
// is the one that has an arm out - the monster walks in it for his own reason.
const SCARECROW = castReader({ head: SCARECROW_HEAD, key: SCARECROW_KEY, walk: TORSO_REACH });

// --- the props ---------------------------------------------------------------

// A potted mum in the potted plant's place, in the same pot and the same
// eleven pixels wide.
const MUM = compile([
  '...OoOyO...',
  '..OoyOoOy..',
  '.OyOoOyOoO.',
  'OoOyOoOyOoO',
  'yOoOyOoOyOo',
  'OoyOoOyOoyO',
  '.OoOyOoOyO.',
  '..gOoOyOg..',
  '.gGg.g.gGg.',
  '..gG.g.Gg..',
  '..ttttttt..',
  '..tTTTTTt..',
  '..tTTTTTt..',
  '...tTTTt...',
  '...tTTTt...',
  '....ttt....',
], { O: '#e45c10', o: '#fca044', y: '#f8b800', g: '#006800', G: '#00a800', t: '#881400', T: '#e45c10' });

// A turkey in the lamp's place, from the front, with his tail fanned out
// behind him in three bands. The body is the bookcases' dark wood, which is
// dim against the wall - a brown off the floor would not be there at all - so
// the paler feathers of the wing are what give him a front.
//
// The lamp rather than the cat, because props are handed out in order along
// the room and the cat is the fourth: a library on the four built-in shelves
// has three gaps between them and never gets one. The lamp is second, so
// every room with more than one gap in it has a turkey.
const TURKEY = compile([
  '....aaaaaaa....',
  '..aabbbbbbbaa..',
  '.abbcccccccbba.',
  'abccaaaaaaaccba',
  'abcabbbbbbbacba',
  'abcabdeeedbacba',
  'abcabdkekdbacba',
  'abcabdeyedbacba',
  '.abcddereddcba.',
  '..adddDrDddda..',
  '...dddDDDddd...',
  '...ddDDDDDdd...',
  '....ddddddd....',
  '.....l...l.....',
  '....lll.lll....',
], {
  a: '#a81000',
  b: '#e45c10',
  c: '#f8b800',
  d: '#881400',
  D: '#ac7c00',
  e: '#f87858',
  k: '#1c1c1c',
  y: '#f8b800',
  r: '#f83800',
  l: '#fca044',
});

// A basket of apples in the crate's place, the crate's ten pixels wide.
const APPLES = compile([
  '...g......',
  '.rrg.rr...',
  'rhrrrhrrr.',
  'rrrRrrrRrr',
  'llllllllll',
  'nNnNnNnNnN',
  'NnNnNnNnNn',
  'nNnNnNnNnN',
  '.NnNnNnNn.',
  '..NNNNNN..',
], { r: '#f83800', R: '#a81000', h: '#f8b8b8', g: '#00a800', n: '#ac7c00', N: '#503000', l: '#f8d878' });

// The rug, in a harvest plaid rather than magenta - the same 26 by 5.
const PLAID_RUG = compile([
  '.rrrrrrrrrrrrrrrrrrrrrrrr.',
  'rRRyRRRRyRRRRyRRRRyRRRRyRr',
  'rOOyOOOOyOOOOyOOOOyOOOOyOr',
  'rRRyRRRRyRRRRyRRRRyRRRRyRr',
  '.rrrrrrrrrrrrrrrrrrrrrrrr.',
], { r: '#503000', R: '#881400', O: '#e45c10', y: '#f8b800' });

// --- the room ----------------------------------------------------------------

// A garland of leaves along the top of a bookcase, the bookcase's own width.
// It is seven pixels tall because that is all there is: the bottom row of the
// shelf's badge is two pixels above the bookcase, and the tallest book on the
// top shelf stops three below the frame. So the vine runs along the frame and
// the leaves hang into the daylight over the books, and touch neither.
const GARLAND = compile([
  '........v...........v...........v...........v...........v...........v........',
  'vvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvv',
  'r.r.r.......y.y.y.......o.o.o.......r.r.r.......y.y.y.......o.o.o.......r.r.r',
  'rrRrr.o.o.o.yyYyy.r.r.r.ooOoo.y.y.y.rrRrr.o.o.o.yyYyy.r.r.r.ooOoo.y.y.y.rrRrr',
  '.rRr..ooOoo..yYy..rrRrr..oOo..yyYyy..rRr..ooOoo..yYy..rrRrr..oOo..yyYyy..rRr.',
  '..r....oOo....y....rRr....o....yYy....r....oOo....y....rRr....o....yYy....r..',
  '........o...........r...........y...........o...........r...........y........',
], { r: '#f83800', R: '#a81000', o: '#e45c10', O: '#881400', y: '#f8b800', Y: '#ac7c00', v: '#006800' });

// Where the garland's top row is, above the floor the bookcases stand on: one
// pixel over the bookcase, under its badge.
const GARLAND_AT = CASE_H - GARLAND.height + 1;

// Three ears of ornamental corn tied up by their husks, for the wall in every
// gap - where spooky hangs its candles, and for the same reason: every gap
// already has a prop standing in it, so the floor there is taken.
const CORN = compile([
  '....RRR....',
  '...R...R...',
  '...R...R...',
  'H...RRR...H',
  'hH.hHRHh.Hh',
  '.hHhHRHhHh.',
  '..hHhRhHh..',
  '...hHRHh...',
  'ryohywohvry',
  'oryhoywhyvr',
  'yor.woy.ryv',
  'ryo.ywo.vry',
  'ory.oyw.yvr',
  'yor.woy.ryv',
  'ryo.ywo.vry',
  'ory.oyw.yvr',
  'yor.woy.ryv',
  'ryo.ywo.vry',
  '.r...y...v.',
  '.o...o...y.',
], { R: '#881400', H: '#ac7c00', h: '#f0d0b0', r: '#a81000', y: '#f8b800', o: '#e45c10', w: '#f8d878', v: '#6844fc' });

// How high the corn hangs: clear of anything standing under it, and among
// the furniture rather than up by the signs.
const CORN_AT = 80;

// A hay bale with a pumpkin and a gourd on it, for the two ends of the room -
// the only stretches of floor no prop stands in.
const HAY_BALE = compile([
  '......gg..............',
  '.......g..............',
  '...oOOoOOOo...........',
  '..oOOOoOOOOo....g.....',
  '..oOOOoOOOOo...wWw....',
  '..oOOOoOOOOo..wWwWw...',
  '..oOOOoOOOOo..wWwWw...',
  '...oOOOOOOo....www....',
  '.nnnnnnnnnnnnnnnnnnnn.',
  'nyyYyyyRyyyYyyyRyyyYyn',
  'nyYyyyYRyYyyyyYRyyYyyn',
  'nYyyyYyRyyyYyyyRyYyyYn',
  'nyyYyyyRyYyyyYyRyyyyyn',
  'nyYyyYyRyyyYyyyRyyYyyn',
  'nyyyYyyRyYyyyyYRyYyyyn',
  'nYyyyyYRyyyYyyyRyyyYyn',
  '.nnnnnnRnnnnnnnRnnnnn.',
], {
  n: '#ac7c00',
  y: '#f8d878',
  Y: '#f8b800',
  R: '#881400',
  g: '#006800',
  o: '#881400',
  O: '#e45c10',
  w: '#b8f818',
  W: '#00a800',
});

// A leaf, flat on and turned edge-on, in three colours. The frames are what
// make it tumble as it falls.
const LEAF_FRAMES = [
  [
    '..x...',
    '.xxx.x',
    'xxXxxx',
    '.xXxx.',
    '..Xx..',
    '.X....',
  ],
  [
    '......',
    '...xx.',
    '..xXx.',
    '.xXx..',
    '.xx...',
    'X.....',
  ],
];
const LEAVES = [
  strip(LEAF_FRAMES, { x: '#f83800', X: '#a81000' }),
  strip(LEAF_FRAMES, { x: '#e45c10', X: '#881400' }),
  strip(LEAF_FRAMES, { x: '#f8b800', X: '#ac7c00' }),
];
const LEAF_H = LEAF_FRAMES[0].length;

// One leaf to this many pixels of room. They are in the room rather than on
// the screen so that one lying on the floor stays where it fell while the
// camera follows the reader past it, and that makes their number go with the
// width of the library: a television's screenful has ten or so in it, and
// forty-five bookcases is some ninety.
const LEAF_EVERY = 40;

function Room({ level, worldH, ceiling }) {
  const floor = FLOOR_H - 1; // where the bookcases stand, up from the stage's foot
  const out = [];
  const last = level.gaps.length - 1;

  level.gaps.forEach((gap, i) => {
    const middle = (sprite) => Math.round(gap.from + (gap.to - gap.from - sprite.width) / 2);
    out.push(
      <i
        key={`corn-${i}`}
        className="eb-corn"
        style={{ left: `${middle(CORN)}px`, bottom: `${floor + CORN_AT}px`, ...sizedSprite(CORN) }}
      />
    );
    if (i === 0 || i === last) {
      out.push(
        <i
          key={`hay-${i}`}
          className="eb-hay"
          style={{ left: `${middle(HAY_BALE)}px`, bottom: `${floor}px`, ...sizedSprite(HAY_BALE) }}
        />
      );
    }
  });

  level.cases.forEach((entry, i) => {
    out.push(
      <i
        key={`garland-${i}`}
        className="eb-garland"
        style={{ left: `${entry.x}px`, bottom: `${floor + GARLAND_AT}px`, ...sizedSprite(GARLAND) }}
      />
    );
  });

  // Falling leaves, placed by number rather than at random so a display that
  // reloads comes back to the same room. Each one comes down out of the dark
  // under the status bar, swaying, lands somewhere on the boards, lies there a
  // while, and is gone - and starts again from the top.
  const count = Math.max(4, Math.round(level.width / LEAF_EVERY));
  const spacing = level.width / count;
  for (let i = 0; i < count; i++) {
    const x = Math.round((i + 0.5) * spacing + ((i * 17) % 13) - 6);
    const from = ceiling - LEAF_H;
    // Somewhere between the back of the floor and the front of it.
    const lands = worldH - 3 - ((i * 7) % 14) - LEAF_H;
    const drop = lands - from;
    // A fall at ten to fifteen pixels a second takes the first seven tenths
    // of the cycle - see eb-leaf-fall.
    const speed = 10 + ((i * 3) % 6);
    const cycle = drop / speed / 0.7;
    out.push(
      <span
        key={`leaf-${i}`}
        className="eb-leaf"
        style={{
          left: `${x}px`,
          top: 0,
          width: `${LEAVES[0].width}px`,
          height: `${LEAF_H}px`,
          '--eb-from': from,
          '--eb-to': lands,
          animationDuration: `${cycle.toFixed(3)}s`,
          animationDelay: `-${((cycle * ((i * 37) % 100)) / 100).toFixed(3)}s`,
          animationTimingFunction: `steps(${drop}, end)`,
        }}
      >
        <span
          className="eb-leaf-sway"
          style={{ animationDuration: `${2.4 + ((i * 5) % 7) * 0.2}s`, animationDelay: `-${i % 3}s` }}
        >
          <i
            className="eb-leaf-art"
            style={{ ...stripStyle(LEAVES[i % LEAVES.length]), animationDuration: `${500 + ((i * 3) % 4) * 100}ms` }}
          />
        </span>
      </span>
    );
  }

  return out;
}

// --- the screen --------------------------------------------------------------

// A goose flying left, wings up and wings down. The neck and head are the
// outline's black, which is the wall's darkness; the white of the cheek and
// the grey of the wings are what say goose at this size.
const GOOSE = [
  [
    '.........ll.',
    '........lll.',
    '.......lll..',
    '......lll...',
    'kwkkggglgggk',
    '....gggggg..',
    '............',
    '............',
    '............',
  ],
  [
    '............',
    '............',
    '............',
    '............',
    'kwkkgggggggk',
    '....ggglggg.',
    '......lll...',
    '.......lll..',
    '........ll..',
  ],
];

// Five geese in a V, pointing the way they fly, and every other one a
// wingbeat out of step with its neighbours, as a skein really is. A flat V,
// because on a television there are about thirty pixels of wall between the
// status bar and the shelf signs, and a skein crossing a sign is in front of
// the one thing on the wall with writing on it.
const SKEIN_AT = [[0, 8], [13, 4], [13, 12], [26, 0], [26, 16]];
function skein(frames) {
  const width = Math.max(...SKEIN_AT.map(([x]) => x)) + frames[0][0].length;
  const height = Math.max(...SKEIN_AT.map(([, y]) => y)) + frames[0].length;
  const rows = Array.from({ length: height }, () => Array(width).fill('.'));
  SKEIN_AT.forEach(([x, y], i) => {
    frames[i % 2].forEach((row, dy) => {
      [...row].forEach((ch, dx) => {
        if (ch !== '.') rows[y + dy][x + dx] = ch;
      });
    });
  });
  return rows.map((row) => row.join(''));
}
const SKEIN = strip(
  [skein(GOOSE), skein([GOOSE[1], GOOSE[0]])],
  { l: '#bcbcbc', k: '#1c1c1c', g: '#7c7c7c', w: '#f8f8f8' }
);

// How far below the status bar the skein crosses, how fast, and how long a
// cycle is - as a multiple of the crossing, with a floor in seconds, for the
// reason spooky's bats have one.
const FLIGHT = { below: 2, speed: 36, every: 5, atLeast: 50, offset: 0.25 };

function Screen({ worldW, ceiling }) {
  const w = SKEIN.width;
  const distance = worldW + w;
  const cycle = Math.max(FLIGHT.atLeast, (distance / FLIGHT.speed) * FLIGHT.every);
  return (
    <span
      className="eb-skein"
      style={{
        top: `${ceiling + FLIGHT.below}px`,
        left: 0,
        width: `${w}px`,
        height: `${SKEIN.height}px`,
        '--eb-from': worldW,
        '--eb-to': -w,
        animationDuration: `${cycle.toFixed(3)}s`,
        animationDelay: `-${(cycle * FLIGHT.offset).toFixed(3)}s`,
        animationTimingFunction: `steps(${distance}, end)`,
      }}
    >
      <i className="eb-skein-wings" style={stripStyle(SKEIN)} />
    </span>
  );
}

export default {
  id: 'harvest',
  from: [11, 1],
  to: thanksgiving,
  reader: SCARECROW,
  props: { plant: MUM, lamp: TURKEY, crate: APPLES, rug: PLAID_RUG },
  Room,
  Screen,
};
